import React, { useEffect, useRef, useState } from "react";
import prefersReducedMotion from "../../utility/prefersReducedMotion";

interface RevealProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  anim?: "up" | "left" | "right" | "zoom";
  delay?: number;
}

type Phase = "idle" | "in" | "done";

export default function Reveal({
  as: Tag = "div",
  anim = "up",
  delay = 0,
  className = "",
  style,
  children,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [phase, setPhase] = useState<Phase>(() =>
    prefersReducedMotion() || !("IntersectionObserver" in window) ? "done" : "idle"
  );

  useEffect(() => {
    if (phase !== "idle" || !ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setPhase("in");
        io.disconnect();
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [phase]);

  // Drop the reveal classes once the animation ends so hover transforms still work
  useEffect(() => {
    if (phase !== "in") return;
    const timer = window.setTimeout(() => setPhase("done"), delay + 1100);
    return () => window.clearTimeout(timer);
  }, [phase, delay]);

  if (phase === "done") {
    return (
      <Tag ref={ref} className={className} style={style} {...rest}>
        {children}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      className={`rv ${phase === "in" ? "in" : ""} ${className}`}
      data-anim={anim}
      style={{ ["--d" as string]: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
